import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import { BOOKING_URL, STACK_QUIZ, WHITE_PAPER_URL, answersFromSearch, score, searchWithAnswers } from './stackQuiz';
import type { Answers, LayerResult } from './stackQuiz';

const SITE_URL = 'https://www.greyfielddata.com';
const NAME = 'Ecommerce data stack quiz';
const TITLE = 'Build your ecommerce data stack: a free quiz';
const DESCRIPTION = 'Ten questions about your channels, team, and reporting today. Get one pick per layer, from ingestion and warehouse to dbt and BI, the reasons for each, and the phase to start with.';
const COVER = '/images/downloads/ecommerce-data-stack-white-paper-cover.webp';

const READ_NEXT = [
  { href: '/blog/ecommerce-data-stack-tools-mapped', label: 'The ecommerce data stack, mapped: 57 tools' },
  { href: '/blog/modern-data-stack-tools-mapped', label: 'The modern data stack, mapped by job' },
  { href: '/blog/ecommerce-semantic-layer', label: 'The ecommerce metric dictionary' },
  { href: '/tools/ecommerce-analytics-tool-quiz', label: 'Not sure you need a stack yet? Take the ecommerce analytics setup quiz' },
  { href: '/tools/bi-tool-quiz', label: 'Compare BI tools in more depth with the BI tool quiz' },
];

function CheckIcon() {
  return (
    <svg className="mt-1 h-4 w-4 flex-none rounded-full bg-[#e6f4ec] p-0.5 text-[#14532d]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

function LayerRow({ entry, notes, shaded }: { entry: LayerResult; notes: string[]; shaded: boolean }) {
  return (
    <li className={`grid gap-2 border-t border-slate-200 px-5 py-5 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-6 sm:px-8 ${shaded ? 'bg-slate-50' : ''}`}>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#1a4e94]">{entry.label}</p>
        {entry.optional && <p className="mt-1 text-xs text-slate-500">Optional</p>}
      </div>
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-[#0a1628]">{entry.name}</h3>
        <p className="mt-1 leading-7 text-slate-600">{entry.summary}</p>
        <ul className="mt-3 space-y-2">
          {entry.reasons.map((reason) => (
            <li key={reason} className="flex gap-2.5 text-[15px] leading-6 text-slate-700"><CheckIcon /><span>{reason}</span></li>
          ))}
        </ul>
        {notes.map((note) => <p key={note} className="mt-3 rounded-xl bg-[#eaf2fd] px-3.5 py-2.5 text-sm leading-6 text-[#0a1628]">{note}</p>)}
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
          {entry.link && <Link to={entry.link} className="font-semibold text-[#1a4e94] underline underline-offset-4 hover:text-[#0a1628]">{entry.link.startsWith('/compare/') ? 'Read the comparison' : 'Read the guide'}</Link>}
          {entry.runnerUp && <span className="text-slate-500">Runner-up: {entry.runnerUp.name}</span>}
        </div>
        {entry.disclosure && <p className="mt-2 text-sm leading-6 text-slate-500">{entry.disclosure}</p>}
      </div>
    </li>
  );
}

export default function StackQuizPage() {
  const quiz = STACK_QUIZ;
  const total = quiz.questions.length;
  const [answers, setAnswers] = React.useState<Answers>({});
  const [step, setStep] = React.useState(0);
  const [edited, setEdited] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const focusTarget = React.useRef<HTMLHeadingElement | HTMLLegendElement | null>(null);
  const moved = React.useRef(false);
  const outcome = score(answers);
  const showResult = step >= total && outcome.approach !== null;
  const question = quiz.questions[Math.min(step, total - 1)];

  // Read a shared result from the URL once, after hydration.
  React.useEffect(() => {
    const fromUrl = answersFromSearch(window.location.search);
    if (Object.keys(fromUrl).length === 0) return;
    setAnswers(fromUrl);
    const firstMissing = quiz.questions.findIndex((q) => !(q.id in fromUrl));
    setStep(firstMissing === -1 ? total : firstMissing);
  }, [quiz, total]);

  // Only touch the address bar after the visitor answers something, keeping UTMs, the hash, and router state.
  React.useEffect(() => {
    if (!edited) return;
    const search = searchWithAnswers(answers, window.location.search);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
  }, [answers, edited]);

  React.useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    focusTarget.current?.focus();
  }, [step]);

  function go(next: number) {
    moved.current = true;
    setCopied(false);
    setStep(Math.max(0, Math.min(total, next)));
  }

  function choose(value: string) {
    setEdited(true);
    setCopied(false);
    setAnswers((current) => ({ ...current, [question.id]: value }));
  }

  function next(event?: React.FormEvent) {
    event?.preventDefault();
    if (!(question.id in answers)) return;
    go(step + 1);
  }

  function restart() {
    setEdited(true);
    setAnswers({});
    go(0);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}${quiz.path}${searchWithAnswers(answers)}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const answeredCurrent = question.id in answers;
  const progress = showResult ? total : step + 1;
  const approach = outcome.approach;
  const phase = outcome.phase;
  const chips = outcome.layers.filter((entry) => !(entry.layer === 'packaged' && entry.id === 'none') && !(entry.layer === 'activation' && entry.id === 'later') && entry.layer !== 'semantic');

  return (
    <BlogLayout wide>
      <SEO title={TITLE} description={DESCRIPTION} url={`${SITE_URL}${quiz.path}`} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: NAME, url: `${SITE_URL}${quiz.path}` },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">{NAME}</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1a4e94]">Free tool · Ecommerce data stack</p>
        <h1 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight text-[#0a1628] sm:text-5xl">Build your ecommerce data stack</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">Ten questions about your channels, team, and reporting today. Get one pick per layer, from ingestion to BI, the reasons behind each, and the implementation phase to start with.</p>
        <p className="mt-3 text-sm text-slate-500">Your answers stay in your browser. Nothing is sent anywhere. The framework behind it is in the <a href={WHITE_PAPER_URL} className="font-semibold text-[#1a4e94] underline underline-offset-4">free white paper (PDF)</a>.</p>
      </header>

      <div className="mt-10 max-w-3xl">
        <div className="mb-3 flex items-baseline justify-between gap-4 text-sm text-slate-600">
          <span className="font-semibold text-[#0a1628]">{showResult ? 'Your stack' : `Question ${step + 1} of ${total}`}</span>
          <span className="truncate">{showResult ? `${total} of ${total} answered` : question.label}</span>
        </div>
        <div
          role="progressbar"
          aria-label="Quiz progress"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={progress}
          aria-valuetext={showResult ? 'All questions answered' : `Question ${step + 1} of ${total}`}
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        >
          {quiz.questions.map((q, index) => (
            <div key={q.id} className={`h-1.5 rounded-full ${index < progress ? 'bg-[#1a4e94]' : 'bg-slate-200'}`} />
          ))}
        </div>

        {!showResult && (
          <form onSubmit={next} className="mt-6 rounded-3xl border border-slate-200 p-5 sm:p-8">
            <fieldset key={question.id}>
              <legend
                ref={(node) => { focusTarget.current = node; }}
                tabIndex={-1}
                className="scroll-mt-32 font-display text-2xl font-bold leading-8 text-[#0a1628] outline-none"
              >
                {question.text}
              </legend>
              <div className="mt-5 space-y-3">
                {question.options.map((option) => {
                  const id = `stack-${question.id}-${option.value}`;
                  const checked = answers[question.id] === option.value;
                  return (
                    <label
                      key={option.value}
                      htmlFor={id}
                      className={`flex min-h-[3.25rem] cursor-pointer items-start gap-3.5 rounded-2xl border px-4 py-3.5 transition focus-within:ring-2 focus-within:ring-[#1a4e94] focus-within:ring-offset-2 sm:px-5 ${checked ? 'border-[#1a4e94] bg-[#eaf2fd] shadow-[inset_0_0_0_1px_#1a4e94]' : 'border-slate-300 bg-white hover:border-[#1a4e94]'}`}
                    >
                      <input
                        id={id}
                        type="radio"
                        name={`stack-${question.id}`}
                        value={option.value}
                        checked={checked}
                        onChange={() => choose(option.value)}
                        className="mt-1 h-[18px] w-[18px] flex-none accent-[#1a4e94]"
                        aria-describedby={option.hint ? `${id}-hint` : undefined}
                      />
                      <span className="min-w-0">
                        <span className={`block font-semibold ${checked ? 'text-[#0a1628]' : 'text-slate-900'}`}>{option.label}</span>
                        {option.hint && <span id={`${id}-hint`} className={`mt-0.5 block text-sm ${checked ? 'text-slate-700' : 'text-slate-500'}`}>{option.hint}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <div className="mt-6 flex items-center justify-between gap-3">
              {step > 0 ? (
                <button type="button" onClick={() => go(step - 1)} className="min-h-[48px] rounded-xl border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-[#1a4e94]">Back</button>
              ) : <span />}
              <button
                type="submit"
                disabled={!answeredCurrent}
                className="min-h-[48px] rounded-xl bg-[#1a4e94] px-6 font-semibold text-white hover:bg-[#153f78] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
              >
                {step === total - 1 ? 'See my stack' : 'Next question'}
              </button>
            </div>
            {!answeredCurrent && <p className="mt-3 text-right text-xs text-slate-500">Pick an answer to continue.</p>}
          </form>
        )}
      </div>

      {showResult && approach && phase && (
        <div className="max-w-4xl">
          <section aria-labelledby="stack-result" className="mt-6 overflow-hidden rounded-3xl border border-slate-200">
            <div className="bg-[#0a1628] p-6 text-white sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#93b4e6]">Your recommended stack</p>
              <h2 id="stack-result" ref={(node) => { focusTarget.current = node; }} tabIndex={-1} className="mt-3 scroll-mt-32 font-display text-2xl font-bold leading-tight outline-none sm:text-3xl">{approach.name}</h2>
              <p className="mt-3 leading-7 text-slate-300">{approach.summary}</p>
              <ul className="mt-4 space-y-1.5 text-[15px] leading-6 text-slate-200">
                {approach.reasons.map((reason) => <li key={reason}>{reason}</li>)}
              </ul>
              <ul className="mt-5 flex flex-wrap gap-2" aria-label="Your picks">
                {chips.map((entry) => (
                  <li key={entry.layer} className="rounded-full border border-[#2c4a75] bg-[#13284a] px-3 py-1.5 text-sm">{entry.name.replace(/,.*$/, '')}</li>
                ))}
              </ul>
              {approach.disclosure && <p className="mt-4 text-sm leading-6 text-slate-400">{approach.disclosure}</p>}
            </div>
            <ol className="list-none">
              {outcome.layers.map((entry, index) => (
                <LayerRow key={entry.layer} entry={entry} shaded={index % 2 === 1} notes={entry.layer === 'ingestion' ? outcome.ingestionNotes ?? [] : []} />
              ))}
            </ol>
            <div className="mx-5 mb-6 rounded-2xl bg-[#e6f4ec] p-5 text-[#14532d] sm:mx-8">
              <p className="text-xs font-bold uppercase tracking-[0.12em]">Start with</p>
              <h3 className="mt-1.5 text-xl font-semibold">{phase.name}</h3>
              <p className="mt-1.5 leading-7">{phase.summary}</p>
              <ul className="mt-2 space-y-1 text-[15px] leading-6">
                {phase.reasons.map((reason) => <li key={reason}>{reason}</li>)}
              </ul>
            </div>
          </section>

          <section aria-labelledby="paper-heading" className="mt-8 grid items-center gap-5 rounded-3xl border border-slate-200 p-5 sm:grid-cols-[132px_minmax(0,1fr)] sm:p-6">
            <a href={WHITE_PAPER_URL} className="block w-28 sm:w-[132px]">
              <img src={COVER} alt="Cover of the white paper The Ecommerce and Consumer Brand Data Stack" width={816} height={1056} loading="lazy" className="h-auto w-full rounded-md border border-slate-200 shadow-sm" />
            </a>
            <div className="min-w-0">
              <h2 id="paper-heading" className="font-display text-xl font-bold text-[#0a1628]">The Ecommerce and Consumer Brand Data Stack</h2>
              <p className="mt-2 leading-7 text-slate-600">The white paper behind this quiz: the seven layers, recommended stacks for each stage, and the five-phase plan with deliverables, owners, acceptance checks, and risks.</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <a href={WHITE_PAPER_URL} download className="inline-flex min-h-[48px] items-center rounded-xl bg-[#1a4e94] px-5 font-semibold text-white hover:bg-[#153f78]">Download the white paper</a>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[48px] items-center rounded-xl border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-[#1a4e94]">Book a free call</a>
              </div>
            </div>
          </section>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button type="button" onClick={copyLink} className="min-h-[48px] rounded-xl border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-[#1a4e94]">{copied ? 'Link copied' : 'Copy a link to this result'}</button>
            <button type="button" onClick={() => go(0)} className="min-h-[48px] px-2 font-semibold text-[#1a4e94] underline underline-offset-4">Change answers</button>
            <button type="button" onClick={restart} className="min-h-[48px] px-2 font-semibold text-slate-600 underline underline-offset-4">Start over</button>
          </div>
          <p role="status" className="sr-only">{copied ? 'Link copied to the clipboard.' : ''}</p>
        </div>
      )}

      <section className="mt-14 grid gap-10 border-t border-slate-200 pt-12 lg:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0a1628]">How the scoring works</h2>
          <ul className="mt-5 space-y-3 leading-7 text-slate-600">
            <li>Each answer gives points to the picks it suits, layer by layer, and takes points from the ones it rules out.</li>
            <li>Layers are decided in order, so the stack fits together: Snowflake semantic views only appear with Snowflake, and a warehouse build does not also get a packaged tool.</li>
            <li>The reasons shown are the answers that counted most toward each pick. The starting phase comes from what you have today and whether your teams agree on revenue.</li>
            <li>Greyfield Data builds ecommerce warehouses and sells Sigma consulting. To keep that from tilting the result, ties go to the simpler, cheaper option, and Sigma is listed last.</li>
          </ul>
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0a1628]">Use it as a starting point</h2>
          <p className="mt-5 leading-7 text-slate-600">A quiz cannot see your data, contracts, or team. Check each vendor's connector list for your exact sources, confirm current pricing with the vendor, and try the stack on one closed month of your own data before you commit.</p>
          <ul className="mt-6 space-y-2">
            {READ_NEXT.map((link) => (
              <li key={link.href}><Link to={link.href} className="font-semibold text-[#1a4e94] underline underline-offset-4">{link.label}</Link></li>
            ))}
            <li><Link to="/resources" className="font-semibold text-[#1a4e94] underline underline-offset-4">More free tools</Link></li>
          </ul>
        </div>
      </section>
    </BlogLayout>
  );
}
